import { FC, ForwardRefExoticComponent, Ref, forwardRef } from 'react';
import * as RadixTabs from '@radix-ui/react-tabs';
import cn from 'classnames';

import { Badge } from '../badge/Badge';
import { Container } from '../../atoms/container/Container';
import { Text } from '../../atoms/text/Text';

import './Tabs.scss';

interface WithClassNames {
  classNames?: cn.Argument;
}

// Tabs.Root
export interface TabsRootProps extends Omit<RadixTabs.TabsProps, 'onClick'>, WithClassNames {
  /**
   * Define the styles of the tabs
   * @default pills
   */
  variant?: TabsVariant;
  /**
   * Stretch the component across the full width of its parent.
   * @default false
   */
  stretch?: boolean;
  /**
   * The ref to the HTML DOM element.
   */
  ref?: Ref<HTMLDivElement>;
}

export const TabsRoot: FC<TabsRootProps> = forwardRef<HTMLDivElement, TabsRootProps>(
  ({ children, classNames, stretch, variant = 'pills', ...props }, ref: TabsRootProps['ref']) => (
    <RadixTabs.Root
      {...props}
      ref={ref}
      className={cn(
        {
          ['lsux-tabs']: true,
          [`lsux-tabs--stretch`]: stretch,
          [`lsux-tabs--variant-${variant}`]: variant,
        },
        classNames,
      )}
    >
      {children}
    </RadixTabs.Root>
  ),
);
TabsRoot.displayName = 'TabsRoot';

// Tabs.List
export type TabsListProps = RadixTabs.TabsListProps & WithClassNames;
export const TabsList: FC<TabsListProps> = ({ children, classNames, ...props }) => (
  <RadixTabs.List {...props} className={cn('lsux-tabs-list', classNames)}>
    {children}
  </RadixTabs.List>
);

// Tabs.Trigger
export interface TabsTriggerProps extends Omit<RadixTabs.TabsTriggerProps, 'onClick'>, WithClassNames {
  /**
   * Text value to be rendered
   */
  children: string;
  /**
   * The onClick handler for the tabs
   */
  onClick?: () => void;
  /**
   * Option to show or hide badges
   * @default false
   */
  showBadge?: boolean;
  /**
   * The text to be rendered in the badge
   */
  badgeText?: string;
}
export const TabsTrigger: FC<TabsTriggerProps> = ({
  children = '',
  classNames,
  onClick,
  showBadge = false,
  badgeText = '',
  ...props
}) => (
  <RadixTabs.Trigger {...props} className={cn('lsux-tabs-trigger', classNames)} onClick={onClick}>
    <Container flexbox flexDirection="row" alignItems="center" background="none">
      <Text textWeight="semibold">{children}</Text>
      {showBadge && (
        <Badge
          variant="important"
          circle={true}
          noBorder={true}
          bold={true}
          small={true}
          text={badgeText}
          classNames={cn({
            'lsux-badge--dot': badgeText === '',
            'ml-2': true,
          })}
        />
      )}
    </Container>
  </RadixTabs.Trigger>
);

// Tabs.Content
export type TabsContentProps = RadixTabs.TabsContentProps & WithClassNames;
export const TabsContent: FC<TabsContentProps> = ({ children, classNames, ...props }) => (
  <RadixTabs.Content {...props} className={cn('lsux-tabs-content', classNames)}>
    {children}
  </RadixTabs.Content>
);

export type TabsVariant = 'pills' | 'tabs' | 'bar';

export interface TabsProps extends TabsRootProps {
  /**
   * Names of the tabs
   * @description These are the values that will be rendered in the tabs.
   * @deprecated Use `TabsTrigger` as `children` instead
   */
  tabNames: string[];
  /**
   * Index in the array of the current active tab
   * @description This is used to determine which tab is active. Must be used with `tabNames` prop.
   * @deprecated Use `value` instead
   */
  activeIndex: number;
  /**
   * The onClick handler for the tabs
   */
  onClick?: (index: number) => void;
  /**
   * The ref to the HTML DOM element.
   */
  ref?: Ref<HTMLDivElement>;
}

const TabsComponent = forwardRef<HTMLDivElement, Omit<TabsProps, 'ref'>>(
  ({ tabNames, activeIndex, value, onClick, ...props }: TabsProps, ref: TabsProps['ref']) => {
    function handleOnClick() {
      if (onClick) {
        return tabNames.indexOf(tabNames[activeIndex]);
      }
    }
    return (
      <TabsRoot {...props} value={tabNames[activeIndex] ?? value} ref={ref}>
        <TabsList>
          {tabNames.map((name, index) => (
            <TabsTrigger value={name} key={index.toString()} onClick={handleOnClick}>
              {name}
            </TabsTrigger>
          ))}
        </TabsList>
      </TabsRoot>
    );
  },
);

TabsComponent.displayName = 'Tabs';

type TabsComp = ForwardRefExoticComponent<TabsProps> & {
  Root: FC<TabsRootProps>;
  List: FC<TabsListProps>;
  Trigger: FC<TabsTriggerProps>;
  Content: FC<TabsContentProps>;
};

/**
 * Tabs allow for an easy way to organize similar content into groups, without having to use separate pages to do so.
 * Tabs should be placed in a consistent location across the screens and render content without any page refresh.
 */
export const Tabs = TabsComponent as TabsComp;

Tabs.Root = TabsRoot;
Tabs.List = TabsList;
Tabs.Trigger = TabsTrigger;
Tabs.Content = TabsContent;
